import { X, Stethoscope, Soup, Armchair, Star } from '@/components/icons'
import { Button } from './ui/button'
import { Card } from '@/components/ui/card'
import { DetailSkeleton } from './ui/skeleton-presets'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { usePassengerGetById } from '@/lib/hooks'
import { getIso2ByCountryName } from '@/lib/phone'
import { CountryFlag } from './ui/country-flag'
import { getInitials } from '@/lib/utils'
import { ProfileAvatar } from './ProfileAvatar'
import { PhoneLink, EmailLink } from './ContactLink'
import dayjs from 'dayjs'

interface PaxBasicDetailDrawerProps {
  isOpen: boolean
  onClose: () => void
  passengerId: string | null
}

export function PaxBasicDetailDrawer({
  isOpen,
  onClose,
  passengerId,
}: PaxBasicDetailDrawerProps) {
  const { data: passenger, isPending } = usePassengerGetById(
    passengerId ?? undefined
  )

  const fullName = passenger
    ? `${passenger.firstName || ''} ${passenger.lastName || ''}`.trim() || 'Unknown'
    : ''

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className="bg-surface-L0 max-h-[85dvh] flex flex-col">
        <DrawerHeader className="flex items-center justify-between p-4">
          <DrawerTitle className="text-lg font-semibold text-neutral-950">
            Passenger Details
          </DrawerTitle>
          <Button
            variant="secondary"
            size="icon"
            roundness="round"
            className="size-8"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {isPending ? (
            <DetailSkeleton rows={6} />
          ) : passenger ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col items-center gap-3">
                <ProfileAvatar
                  className="size-18"
                  photoS3Key={passenger.photoS3Key}
                  avatarColor={passenger.avatarColor}
                  initials={getInitials(passenger.firstName, passenger.lastName)}
                  fallbackClassName="text-[35px] font-semibold text-background"
                />
              </div>
              <Card className="p-4">
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Name</span>
                    <span>{fullName}</span>
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Nationality</span>
                    <span className="inline-flex items-center gap-1.5">
                      {passenger.nationality ? (
                        <>
                          <CountryFlag
                            iso2={getIso2ByCountryName(passenger.nationality)}
                            className="w-4"
                          />
                          {passenger.nationality}
                        </>
                      ) : (
                        '-'
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Telephone</span>
                    <PhoneLink phone={passenger.phone} />
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Email</span>
                    <EmailLink email={passenger.email} />
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Date of Birth</span>
                    <span>
                      {passenger.dateOfBirth
                        ? dayjs.utc(passenger.dateOfBirth).format('MMM D, YYYY')
                        : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Role</span>
                    <span>{passenger.role || '-'}</span>
                  </div>
                </div>
              </Card>

              <Card className="px-4 py-4">
                <div className="flex flex-col gap-4">
                  <div className="flex gap-2 items-center">
                    <div className="size-8 rounded-full bg-background-brand-subtle flex items-center justify-center">
                      <Stethoscope className="size-4 text-content-brand-bold" />
                    </div>
                    <p className="text-sm font-medium">Medical Information</p>
                  </div>
                  <p className="text-xs whitespace-pre-wrap wrap-break-word">
                    {[passenger.medicalInfo, passenger.allergies]
                      .filter(Boolean)
                      .join('\n') || '-'}
                  </p>
                </div>
              </Card>

              <Card className="px-4 py-4">
                <div className="flex flex-col gap-4">
                  <div className="flex gap-2 items-center">
                    <div className="size-8 rounded-full bg-background-brand-subtle flex items-center justify-center">
                      <Soup className="size-4 text-content-brand-bold" />
                    </div>
                    <p className="text-sm font-medium">Food Preferences</p>
                  </div>
                  <p className="text-xs whitespace-pre-wrap wrap-break-word">{passenger.foodPreferences || '-'}</p>
                </div>
              </Card>

              <Card className="px-4 py-4">
                <div className="flex flex-col gap-4">
                  <div className="flex gap-2 items-center">
                    <div className="size-8 rounded-full bg-background-brand-subtle flex items-center justify-center">
                      <Armchair className="size-4 text-content-brand-bold" />
                    </div>
                    <p className="text-sm font-medium">Cabin Preferences</p>
                  </div>
                  <p className="text-xs whitespace-pre-wrap wrap-break-word">{passenger.cabinPreferences || '-'}</p>
                </div>
              </Card>

              <Card className="px-4 py-4">
                <div className="flex flex-col gap-4">
                  <div className="flex gap-2 items-center">
                    <div className="size-8 rounded-full bg-background-brand-subtle flex items-center justify-center">
                      <Star className="size-4 text-content-brand-bold" />
                    </div>
                    <p className="text-sm font-medium">Beauty Preferences</p>
                  </div>
                  <p className="text-xs whitespace-pre-wrap wrap-break-word">{passenger.beautyPreferences || '-'}</p>
                </div>
              </Card>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <p className="text-gray-500">Passenger not found</p>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
